import { useCallback, useState } from 'react';
import { apiRequest } from '../utils/apiUtils';
import type { DiscoverMatch, DiscoverMatchesFilters } from '@myfutbolapp/shared';

export interface UseDiscoverMatchesResult {
  matches: DiscoverMatch[];
  loading: boolean;
  error: string | null;
  searched: boolean;
  search: (filters: DiscoverMatchesFilters) => Promise<void>;
  reset: () => void;
}

function buildQuery(params: DiscoverMatchesFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (params.date?.trim()) p.set('date', params.date.trim());
  if (params.timeFrom?.trim()) p.set('timeFrom', params.timeFrom.trim());
  if (params.timeTo?.trim()) p.set('timeTo', params.timeTo.trim());
  if (params.matchGenderType) p.set('matchGenderType', params.matchGenderType);
  if (params.skillLevel?.trim()) p.set('skillLevel', params.skillLevel.trim());

  const lat = parseFloat(params.latitude || '');
  const lng = parseFloat(params.longitude || '');
  const geoOk = !Number.isNaN(lat) && !Number.isNaN(lng);
  const zoneTrim = (params.zone || '').trim();

  if (geoOk) {
    p.set('latitude', String(lat));
    p.set('longitude', String(lng));
    p.set('radiusKm', params.radiusKm?.trim() || '15');
    if (zoneTrim.length >= 2 && params.omitZoneFilter !== '1') {
      p.set('zone', zoneTrim);
    }
  } else if (zoneTrim.length >= 2) {
    p.set('zone', zoneTrim);
  }
  return p;
}

export function useDiscoverMatches(): UseDiscoverMatchesResult {
  const [matches, setMatches] = useState<DiscoverMatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const search = useCallback(async (filters: DiscoverMatchesFilters) => {
    const q = buildQuery(filters);
    const hasGeo = q.has('latitude') && q.has('longitude');
    const hasZone = q.has('zone');
    if (!hasGeo && !hasZone) {
      setError('Ingresá una zona para buscar partidos.');
      setMatches([]);
      setSearched(true);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await apiRequest<{ matches: DiscoverMatch[]; searchMode?: string }>(
        `/matches/public/discover?${q.toString()}`
      );
      if (res.error) {
        setError(res.error);
        setMatches([]);
      } else {
        setMatches(res.data?.matches || []);
      }
    } catch (e) {
      setError('No pudimos buscar partidos. Probá de nuevo.');
      setMatches([]);
    } finally {
      setLoading(false);
      setSearched(true);
    }
  }, []);

  const reset = useCallback(() => {
    setMatches([]);
    setError(null);
    setSearched(false);
  }, []);

  return { matches, loading, error, searched, search, reset };
}
